<template>
    <div
        class="layout-container flex justify-center"
        :class="{
            column: navPos === 'top' || navPos === 'bottom',
            boxed: boxed,
            'footer-above': footer === 'above',
            'content-only': !navPos,
            'digital-display-page': hasDigitalDisplayPage()
        }"
    >
        <bus />
        <branding />

        <transition name="fade">
            <div class="splash-screen" v-if="splashScreen">
                <div class="wrap">
                    <img :src="logo" class="logo" alt="logo" />
                    <img src="/images/spinner.svg" alt="loading-image" />
                </div>
            </div>
        </transition>

        <vertical-nav
            :position="navPos"
            :collapse-nav="collapseNav"
            :open-sidebar.sync="openSidebar"
            @collapse-nav-toggle="collapseNav = !collapseNav"
            @push-page="closeSidebar"
            v-if="navPos === 'left'"
        />

        <div class="container flex column box grow">
            <div class="header" v-if="toolbar === 'top'">
                <Toolbar
                    @toggle-sidebar="openSidebar = !openSidebar"
                    :menu-burger="navPos"
                />
            </div>
            <horizontal-nav
                :position="navPos"
                @push-page="closeSidebar"
                v-if="navPos === 'top'"
            />
            <div class="header" v-if="toolbar === 'bottom'">
                <Toolbar
                    @toggle-sidebar="openSidebar = !openSidebar"
                    :menu-burger="navPos"
                />
            </div>

            <!--<breadcrumbs/>-->

            <div class="top-banner" v-if="showSuspendedMessage">
                <el-alert
                    :closable="false"
                    center
                    show-icon
                    title="Please send payment to resume service."
                    type="error"
                >
                </el-alert>
            </div>

            <div class="top-banner" v-if="showPastDueInvoiceMessage">
                <el-alert
                    :closable="false"
                    center
                    show-icon
                    title="You have an invoice approaching terms"
                    type="warning"
                >
                </el-alert>
            </div>

            <div class="top-banner" v-if="showSessionRemaining">
                <el-alert
                    :closable="false"
                    :title="`If no action performed, you will be logged out in ${sessionRemainingText}.`"
                    center
                    show-icon
                    type="warning"
                >
                </el-alert>
            </div>

            <div class="main box grow flex">
                <span
                    class="main-out-border--top-left"
                    v-if="roundedCorners"
                ></span>
                <span
                    class="main-out-border--top-right"
                    v-if="roundedCorners"
                ></span>
                <span
                    class="main-out-border--bottom-left"
                    v-if="roundedCorners"
                ></span>
                <span
                    class="main-out-border--bottom-right"
                    v-if="roundedCorners"
                ></span>
                <transition :name="viewAnimation" mode="out-in">
                    <router-view class="view box grow" />
                </transition>
            </div>

            <horizontal-nav
                :position="navPos"
                @push-page="closeSidebar"
                v-if="navPos === 'bottom'"
                style="margin-bottom: 0"
            />

            <!--<Footer v-if="footer === 'below'" :position="footer"/>-->
        </div>

        <vertical-nav
            :position="navPos"
            :collapse-nav="collapseNav"
            :open-sidebar.sync="openSidebar"
            @collapse-nav-toggle="collapseNav = !collapseNav"
            @push-page="closeSidebar"
            v-if="navPos === 'right'"
        />

        <policy-dialog ref="policyDialog"></policy-dialog>
        <logged-out-dialog ref="loggedOutDialog"></logged-out-dialog>
        <custom-notification-popup
            :messages="custom_popup_messages"
            ref="customNotificationPopup"
            v-if="custom_popup_messages.length"
        />
    </div>
</template>

<script>
import Branding from "@/components/Branding";
import LayoutPicker from "@/components/layout-picker.vue";
import LoggedOutDialog from "@/components/LoggedOutDialog";
import CustomNotificationType from "@/constants/custom-notification-types";
import Footer from "@/core/footer.vue";
import HorizontalNav from "@/core/horizontal-nav.vue";
import Toolbar from "@/core/toolbar.vue";
import VerticalNav from "@/core/vertical-nav.vue";
import CustomNotificationUtils from "@/utils/custom-notification";
import SessionUtils from "@/utils/session";
import { detect } from "detect-browser";
import Bus from "./Bus.vue";
import CustomNotificationPopup from "./components/CustomNotificationPopup";
import PolicyDialog from "./components/PolicyDialog";
import * as BusEvents from "./constants/bus-events";
import * as Roles from "./constants/roles";
import * as Routes from "./constants/routes";
import Breadcrumbs from "./core/Breadcrumbs";
import auth from "./mixins/auth";
import * as Actions from "./store/constants/actions";
import * as Getters from "./store/constants/getters";
import * as Mutations from "./store/constants/mutations";

const browser = detect();

export default {
    mixins: [auth],
    name: "App",
    components: {
        HorizontalNav,
        VerticalNav,
        Toolbar,
        Footer,
        LayoutPicker,
        Breadcrumbs,
        Bus,
        PolicyDialog,
        CustomNotificationPopup,
        LoggedOutDialog,
        Branding
    },
    data() {
        return {
            collapseNav: false,
            openSidebar: false,
            innerWidth: 0,
            sessionRemainingSeconds: null,
            sessionRemainingText: null,
            sessionRemainingInterval: null,
            custom_popup_messages: []
        };
    },
    computed: {
        navPos() {
            if (
                this.innerWidth <= 768 &&
                (this.$store.getters.navPos === "top" ||
                    this.$store.getters.navPos === "bottom")
            ) {
                return "left";
            }
            return this.$store.getters.navPos;
        },
        toolbar() {
            return this.$store.getters.toolbar;
        },
        footer() {
            return this.$store.getters.footer;
        },
        boxed() {
            return this.$store.getters.boxed;
        },
        roundedCorners() {
            return this.$store.getters.roundedCorners;
        },
        viewAnimation() {
            return this.$store.getters.viewAnimation || "none";
        },
        isLogged() {
            return this.$store.getters.isLogged;
        },
        splashScreen() {
            return this.$store.getters.splashScreen;
        },
        logo() {
            return this.$store.getters[Getters.GET_LOGO];
        },
        hasPastDueInvoice() {
            return (
                this.auth.user &&
                this.$store.state.current_community &&
                this.$store.state.current_community.has_past_due_invoice
            );
        },
        isSuspended() {
            if (this.auth.user && this.$store.state.current_community) {
                return (
                    this.$store.state.current_community.suspended &&
                    this.$store.state.current_community.has_past_due_invoice
                );
            }
            return false;
        },
        showPastDueInvoiceMessage() {
            return (
                !_.get(
                    this.$store.state.current_community,
                    "corporation.disregard_late_invoice"
                ) &&
                !this.isSuspended &&
                this.hasPastDueInvoice
            );
        },
        showSuspendedMessage() {
            return (
                !_.get(
                    this.$store.state.current_community,
                    "corporation.disregard_late_invoice"
                ) && this.isSuspended
            );
        },
        showSessionRemaining() {
            if (!this.auth.user) {
                return false;
            }
            return (
                this.sessionRemainingSeconds &&
                this.sessionRemainingSeconds > 0 &&
                this.sessionRemainingSeconds / 60 <= 5
            );
        }
    },
    created() {
        if (browser && browser.name) {
            document
                .getElementsByTagName("html")[0]
                .classList.add(browser.name);
        }
        window.Bus.$on(BusEvents.SESSION_EXPIRED, () => {
            axios.post(`/logout`).finally(() => {
                console.log("Bus event Session Expired");
                this.$refs.loggedOutDialog.show();
            });
        });
        window.Bus.$once(BusEvents.ERROR_UNAUTHENTICATED, () => {
            console.log("Bus event Unathenticated event");
            this.$refs.loggedOutDialog.show();
        });
    },
    mounted() {
        this.initAutoLogout();
        this.resizeOpenNav();
        window.addEventListener("resize", this.resizeOpenNav);
        if (this.auth.user) {
            this.$store.dispatch(Actions.FETCH_DISCOUNTS);
            this.$store.dispatch(Actions.FETCH_CART);
            this.$store.dispatch(Actions.FETCH_STATES);
            if (this.hasRole(Roles.MAIN_ROLES)) {
                Promise.all([
                    this.$store.dispatch(Actions.FETCH_CORPORATIONS),
                    this.$store.dispatch(Actions.FETCH_COMMUNITIES_MIN)
                    // this.$store.dispatch(Actions.FETCH_USERS)
                ]).then(() => {
                    this.$store.commit(Mutations.SET_PRESET_DATA_FETCHED, true);
                    window.Bus.$emit(BusEvents.PRESET_DATA_FETCHED);
                });
            } else if (this.hasRole(Roles.CORPORATION_OWNER)) {
                Promise.all([
                    this.$store.dispatch(Actions.FETCH_COMMUNITIES_ALL),
                    this.$store.dispatch(Actions.FETCH_USERS_ALL)
                ]).then(() => {
                    this.$store.commit(Mutations.SET_PRESET_DATA_FETCHED, true);
                    window.Bus.$emit(BusEvents.PRESET_DATA_FETCHED);
                });
            }
        }
        if (this.$route.query.redirect) {
            this.$router.push(this.$route.query.redirect);
            // location = this.$route.query.redirect;
        }
        window.Bus.$on("openPolicyDialog", () => {
            this.$refs.policyDialog.show();
        });
        this.updatePageTitle();
        this.checkCustomPopupNotifications();
        this.hasDigitalDisplayPage();

        window.toastBus.$on("show-toast", (options) => {
            this.$toastr("add", {
                title: options.title,
                msg: options.message,
                clickClose: true,
                timeout: 3000,
                position: "toast-top-right",
                type: options.type || "warning"
            });
        });
    },
    watch: {
        $route(to, from) {
            this.updatePageTitle();
        }
    },
    methods: {
        updatePageTitle() {
            document.title =
                `MyCorwin` +
                (_.get(this.$route, "meta.label")
                    ? ` | ${this.$route.meta.label}`
                    : "");
        },
        resizeOpenNav() {
            this.innerWidth = window.innerWidth;
            if (window.innerWidth <= 768) {
                this.collapseNav = false;
            }
        },
        closeSidebar() {
            this.openSidebar = false;
        },
        updateUser() {
            let update = () => {
                setTimeout(() => {
                    axios
                        .get("/user")
                        .then((response) => {
                            this.$store.commit(
                                Mutations.UPDATE_AUTH_USER,
                                response.data
                            );
                        })
                        .catch((e) => {
                            if (e.response.status === 401) {
                                window.location = "/login";
                            }
                        })
                        .finally(() => update());
                }, 1000 * 30);
            };
            update();
        },
        initAutoLogout() {
            if (SessionUtils.remembersUser()) {
                // remember me is enabled for user
                return;
            }
            this.sessionRemainingInterval = setInterval(() => {
                this.sessionRemainingSeconds = SessionUtils.remaining();
                this.sessionRemainingText = SessionUtils.formatRemaining();
                if (this.sessionRemainingText) {
                    //console.log(this.sessionRemainingText);
                }
                if (this.sessionRemainingSeconds === null) {
                    return;
                }
                if (
                    this.sessionRemainingSeconds &&
                    this.sessionRemainingSeconds <= 120
                ) {
                    window.Bus.$emit(BusEvents.ABOUT_TO_LOGOUT);
                }
                if (this.sessionRemainingSeconds <= 0) {
                    window.Bus.$emit(BusEvents.SESSION_EXPIRED);
                    clearInterval(this.sessionRemainingInterval);
                }
            }, 10000);
        },
        async checkCustomPopupNotifications() {
            if (CustomNotificationUtils.shouldNotify()) {
                let message = _.get(
                    this.$store.state,
                    "current_community.popup_message"
                );
                if (message) {
                    this.custom_popup_messages.push(message);
                }
                let response = await axios.get(
                    `/web-api/custom-notification/for-me`,
                    {
                        params: {
                            type: CustomNotificationType.popup.value,
                            all: 1
                        }
                    }
                );
                _.forEach(response.data.data, (notification) =>
                    this.custom_popup_messages.push(notification.body)
                );
            }
            if (this.custom_popup_messages.length) {
                Vue.nextTick(() => {
                    this.$refs.customNotificationPopup.show();
                    CustomNotificationUtils.setNotified(true);
                });
            }
        },
        hasDigitalDisplayPage() {
            if (this.$route.name == Routes.DIGITAL_DISPLAY_URL) {
                return true;
            }
            return false;
        }
    },
    beforeDestroy() {
        window.removeEventListener("resize", this.resizeOpenNav);
    }
};
</script>

<style lang="scss">
@use "../assets/scss/mixins" as *;
@use "../assets/scss/variables" as vars;

.layout-container {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    overflow: hidden;
    background: vars.$background-color;

    .container {
        overflow: hidden;

        .header {
            height: 60px;
            margin: 0 30px;
        }

        .main {
            position: relative;
            overflow: hidden;
            padding: 0 30px;
        }

        .view {
            padding: 20px;
            padding-bottom: 10px;
            padding-top: 0px;
            box-sizing: border-box;
            transition: all 0.4s cubic-bezier(0.55, 0, 0.1, 1);
            backface-visibility: hidden;
            /*-webkit-perspective: 1000px;*/
        }
        .fade-top-in-out-enter {
            opacity: 0;
            transform: translate(0, 30px);
        }
        .fade-top-in-out-leave-active {
            opacity: 0;
            transform: translate(0, 30px);
        }

        .fade-top-enter {
            opacity: 0;
            transform: translate(0, 30px);
        }
        .fade-top-leave-active {
            opacity: 0;
            transform: translate(0, -30px);
        }

        .fade-bottom-in-out-enter {
            opacity: 0;
            transform: translate(0, -30px);
        }
        .fade-bottom-in-out-leave-active {
            opacity: 0;
            transform: translate(0, -30px);
        }

        .fade-bottom-enter {
            opacity: 0;
            transform: translate(0, -30px);
        }
        .fade-bottom-leave-active {
            opacity: 0;
            transform: translate(0, 30px);
        }

        .fade-left-enter {
            opacity: 0;
            transform: translate(30px, 0);
        }
        .fade-left-leave-active {
            opacity: 0;
            transform: translate(-30px, 0);
        }

        .fade-right-enter {
            opacity: 0;
            transform: translate(-30px, 0);
        }
        .fade-right-leave-active {
            opacity: 0;
            transform: translate(30px, 0);
        }

        .fade-enter {
            opacity: 0;
        }
        .fade-leave-active {
            opacity: 0;
        }

        .main-out-border {
            &--top-left,
            &--top-right {
                background: linear-gradient(
                    vars.$background-color,
                    rgba(230, 235, 241, 0)
                );
                height: 16px;
                position: absolute;
                width: 8px;
                z-index: 1;
                top: 4px;
            }
            &--bottom-left,
            &--bottom-right {
                background: linear-gradient(
                    rgba(230, 235, 241, 0),
                    vars.$background-color
                );
                height: 16px;
                position: absolute;
                width: 8px;
                z-index: 1;
                bottom: 4px;
            }

            &--top-left,
            &--bottom-left {
                left: 42px;

                &::after {
                    content: "";
                    height: 5px;
                    position: absolute;
                    right: -4px;
                    top: -4px;
                    width: 12px;
                    box-shadow: 8px 0px 0px 0px vars.$background-color inset;
                    border-top-left-radius: 5px;
                }
            }
            &--top-right,
            &--bottom-right {
                right: 42px;

                &::after {
                    content: "";
                    height: 5px;
                    left: -4px;
                    position: absolute;
                    top: -4px;
                    width: 12px;
                    box-shadow: -8px 0px 0px 0px vars.$background-color inset;
                    border-top-right-radius: 5px;
                }
            }

            &--bottom-left:after {
                border-radius: 0;
                border-bottom-left-radius: 5px;
            }
            &--bottom-right:after {
                border-radius: 0;
                border-bottom-right-radius: 5px;
            }

            &--bottom-left,
            &--bottom-right {
                &::after {
                    top: initial;
                    bottom: -4px;
                }
            }
        }
    }

    &.boxed {
        max-width: 1300px;
        margin: 0 auto;
        box-shadow:
            0px 0px 20px 10px rgba(0, 0, 0, 0.15),
            0px 0px 5px 0px rgba(0, 0, 0, 0.1);
    }

    &.footer-above {
        padding-bottom: 40px;
        position: relative;
    }

    &.content-only {
        .container {
            .main-out-border--top-left,
            .main-out-border--top-right,
            .main-out-border--bottom-left,
            .main-out-border--bottom-right {
                display: none;
            }
        }
    }
}

html:not(.ie) {
    .layout-container {
        .container {
            /*max-width: 1920px;*/
        }
    }
}

@media (min-width: 1920px) {
    .layout-container:not(.boxed) {
        &.column {
            .container {
                margin: 0 auto;
            }
        }
    }
}

@media (max-width: 768px) {
    .layout-container {
        .container {
            /*width: 100%;
                max-width: 100%;
                height: 100%;
                max-height: 100%;*/

            .header {
                height: 50px;
                background: #fff;
                box-shadow: 0px -20px 10px 20px rgba(0, 0, 0, 0.25);
                margin: 0;
                margin-bottom: 5px;

                .toggle-sidebar {
                    box-shadow: none !important;
                }

                .search-box {
                    & > .el-autocomplete {
                        box-shadow: none !important;
                    }
                }
            }

            .main {
                padding: 0 5px;
            }

            .view {
                //padding: 5px;
                max-width: 100%;
                padding: 15px;
                padding-left: 15px;
                padding-right: 15px;
            }
            .main-out-border--top-left,
            .main-out-border--top-right,
            .main-out-border--bottom-left,
            .main-out-border--bottom-right {
                display: none;
            }
        }
    }
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease-out;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.top-banner {
    padding-left: 20px;
    padding-right: 20px;
}
</style>
